import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Copy, Trash2, Plus, ExternalLink, Edit3, Eye } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";

import { api } from "@/api/client";
import { useLang } from "@/context/LangContext";
import { ADMIN } from "@/constants/testIds";

import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { SellerPreviewModal } from "@/components/preview/SellerPreviewModal";
import { ExamPreview } from "@/components/preview/ExamPreview";

const CATEGORIES = [
  { id: "sales", es: "Ventas", en: "Sales" },
  { id: "service", es: "Servicio", en: "Service" },
  { id: "financing", es: "Financiamiento", en: "Financing" },
  { id: "custom", es: "Personalizado", en: "Custom" },
];

const emptyForm = {
  title_es: "", title_en: "", category: "sales",
  time_limit_seconds: 600, passing_score: 70,
  question_ids: [], active: true,
};

export default function AdminExams() {
  const { t, lang } = useLang();
  const [exams, setExams] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null); // exam_id or null
  const [form, setForm] = useState(emptyForm);
  const [previewOpen, setPreviewOpen] = useState(false);

  const load = async () => {
    const [e, q] = await Promise.all([api.get("/admin/exams"), api.get("/admin/questions")]);
    setExams(e.data);
    setQuestions(q.data);
  };
  useEffect(() => { load(); }, []);

  const publicUrl = (slug) => `${window.location.origin}/exam/${slug}`;
  const copy = async (slug) => {
    await navigator.clipboard.writeText(publicUrl(slug));
    toast.success(t("linkCopied"));
  };

  // For custom exams (and edit mode), show all questions so admin can pick
  // from any category. For fresh create with a fixed category, filter.
  const filteredQuestions = useMemo(() => {
    if (editing) return questions;
    if (form.category === "custom") return questions;
    return questions.filter((q) => q.category === form.category);
  }, [editing, questions, form.category]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setOpen(true);
  };

  const openEdit = (exam) => {
    setEditing(exam.exam_id);
    setForm({
      title_es: exam.title_es || "",
      title_en: exam.title_en || "",
      category: exam.category || "sales",
      time_limit_seconds: exam.time_limit_seconds ?? 600,
      passing_score: exam.passing_score ?? 70,
      question_ids: exam.question_ids || [],
      active: exam.active !== false,
    });
    setOpen(true);
  };

  const toggleQ = (qid) => {
    setForm((f) => ({
      ...f,
      question_ids: f.question_ids.includes(qid)
        ? f.question_ids.filter((x) => x !== qid)
        : [...f.question_ids, qid],
    }));
  };

  const submit = async () => {
    if (!form.title_es.trim()) {
      toast.error(lang === "es" ? "Escribe un título" : "Enter a title");
      return;
    }
    if (form.question_ids.length === 0) {
      toast.error(lang === "es" ? "Elige al menos 1 pregunta" : "Pick at least 1 question");
      return;
    }
    try {
      if (editing) {
        await api.put(`/admin/exams/${editing}`, form);
        toast.success(lang === "es" ? "Examen actualizado" : "Exam updated");
      } else {
        await api.post("/admin/exams", form);
        toast.success(lang === "es" ? "Examen creado" : "Exam created");
      }
      setOpen(false);
      setEditing(null);
      setForm(emptyForm);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  const removeExam = async (id) => {
    if (!window.confirm(lang === "es" ? "¿Eliminar examen?" : "Delete exam?")) return;
    await api.delete(`/admin/exams/${id}`);
    load();
  };

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-display font-black text-4xl">{t("exams")}</h1>

        <Button
          data-testid={ADMIN.createExamBtn}
          onClick={openCreate}
          className="brutal-btn bg-canales-red text-white px-4 py-2"
        >
          <Plus className="w-4 h-4 mr-1" /> {t("createExam")}
        </Button>
      </div>

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); setForm(emptyForm); } }}>
        <DialogContent className="max-w-2xl brutal border-black bg-white max-h-[90vh] overflow-auto">
          <DialogHeader>
            <DialogTitle className="font-display font-black text-2xl">
              {editing
                ? (lang === "es" ? "Editar examen" : "Edit exam")
                : t("createExam")}
            </DialogTitle>
          </DialogHeader>
          <div className="grid gap-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Título ES</Label>
                <Input
                  data-testid={ADMIN.examTitleEs}
                  value={form.title_es}
                  onChange={(e) => setForm({ ...form, title_es: e.target.value })}
                />
              </div>
              <div>
                <Label>Title EN</Label>
                <Input
                  data-testid={ADMIN.examTitleEn}
                  value={form.title_en}
                  onChange={(e) => setForm({ ...form, title_en: e.target.value })}
                />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>{t("category")}</Label>
                <Select
                  value={form.category}
                  onValueChange={(v) => setForm({ ...form, category: v, ...(editing ? {} : { question_ids: [] }) })}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c[lang]}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t("time")} ({t("seconds")})</Label>
                <Input
                  data-testid={ADMIN.examTimeLimit}
                  type="number"
                  value={form.time_limit_seconds || ""}
                  onChange={(e) =>
                    setForm({ ...form, time_limit_seconds: e.target.value ? Number(e.target.value) : null })
                  }
                  placeholder="600"
                />
              </div>
              <div>
                <Label>{t("passingScore")} %</Label>
                <Input
                  type="number"
                  value={form.passing_score}
                  onChange={(e) => setForm({ ...form, passing_score: Number(e.target.value) })}
                />
              </div>
            </div>

            {editing && (
              <div className="brutal-sm p-3 bg-canales-cream flex items-center justify-between">
                <div>
                  <div className="font-display font-bold">{lang === "es" ? "Examen activo" : "Exam active"}</div>
                  <div className="text-xs font-body text-neutral-600">
                    {lang === "es"
                      ? "Si lo desactivas, el link público deja de aceptar respuestas."
                      : "If disabled, the public link stops accepting submissions."}
                  </div>
                </div>
                <Switch
                  checked={form.active}
                  onCheckedChange={(v) => setForm({ ...form, active: v })}
                  data-testid="admin-exam-active-toggle"
                />
              </div>
            )}

            <div>
              <Label>
                {lang === "es" ? "Preguntas" : "Questions"} ({form.question_ids.length})
              </Label>
              <div className="mt-2 max-h-72 overflow-auto brutal-sm p-3 bg-canales-cream space-y-2">
                {filteredQuestions.map((q) => {
                  const checked = form.question_ids.includes(q.question_id);
                  return (
                    <label key={q.question_id} className="flex items-start gap-2 cursor-pointer bg-white brutal-sm p-2">
                      <Checkbox checked={checked} onCheckedChange={() => toggleQ(q.question_id)} />
                      <span className="text-sm">
                        <span className="text-xs font-display font-bold uppercase tracking-widest text-canales-red">
                          {q.category}
                        </span>{" · "}
                        <span className="font-display font-bold">{q.topic}</span>{" · "}
                        {q[`prompt_${lang}`]}
                      </span>
                    </label>
                  );
                })}
                {filteredQuestions.length === 0 && (
                  <div className="text-sm font-body text-neutral-500">
                    {lang === "es" ? "No hay preguntas en esta categoría" : "No questions in this category"}
                  </div>
                )}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              onClick={() => setPreviewOpen(true)}
              data-testid="exam-preview-btn"
              className="brutal-btn bg-white text-black px-4 py-2"
            >
              <Eye className="w-4 h-4 mr-1" /> {lang === "es" ? "Vista previa" : "Preview"}
            </Button>
            <Button
              data-testid={ADMIN.createExamSubmit}
              onClick={submit}
              className="brutal-btn bg-canales-blue text-white px-4 py-2"
            >
              {editing
                ? (lang === "es" ? "Guardar cambios" : "Save changes")
                : (lang === "es" ? "Crear" : "Create")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* list */}
      <div className="grid md:grid-cols-2 gap-4">
        {exams.map((e) => (
          <div key={e.exam_id} className={`brutal p-5 bg-white ${e.active === false ? "opacity-70" : ""}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-display font-bold uppercase tracking-widest text-canales-red">
                    {e.category}
                  </span>
                  {e.active === false && (
                    <span className="text-xs font-display font-bold uppercase tracking-widest px-2 py-0.5 bg-neutral-800 text-white rounded-full">
                      {lang === "es" ? "Inactivo" : "Inactive"}
                    </span>
                  )}
                </div>
                <h3 className="font-display font-black text-xl truncate">
                  {lang === "es" ? e.title_es : e.title_en}
                </h3>
                <div className="text-sm font-body text-neutral-600 mt-1">
                  {e.question_ids.length} {lang === "es" ? "preguntas" : "questions"} ·{" "}
                  {e.attempts_count || 0} {lang === "es" ? "intentos" : "attempts"}
                </div>
              </div>
              <QRCodeSVG value={publicUrl(e.slug)} size={72} bgColor="#fff8e7" fgColor="#0B0B14" />
            </div>
            <div className="mt-3 text-xs font-body break-all bg-canales-cream brutal-sm p-2">
              {publicUrl(e.slug)}
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              <button
                onClick={() => copy(e.slug)}
                data-testid={ADMIN.copyLinkBtn(e.slug)}
                className="brutal-btn bg-canales-yellow px-3 py-1 text-sm flex items-center gap-1"
              >
                <Copy className="w-4 h-4" /> {t("copyLink")}
              </button>
              <a
                href={publicUrl(e.slug)}
                target="_blank"
                rel="noreferrer"
                className="brutal-btn bg-white px-3 py-1 text-sm flex items-center gap-1"
              >
                <ExternalLink className="w-4 h-4" /> {lang === "es" ? "Abrir" : "Open"}
              </a>
              <button
                onClick={() => openEdit(e)}
                data-testid={`admin-exam-edit-${e.slug}`}
                className="brutal-btn bg-canales-blue text-white px-3 py-1 text-sm flex items-center gap-1"
              >
                <Edit3 className="w-4 h-4" /> {lang === "es" ? "Editar" : "Edit"}
              </button>
              <button
                onClick={() => removeExam(e.exam_id)}
                className="brutal-btn bg-red-500 text-white px-3 py-1 text-sm flex items-center gap-1"
              >
                <Trash2 className="w-4 h-4" /> {t("delete")}
              </button>
            </div>
          </div>
        ))}
      </div>

      <SellerPreviewModal open={previewOpen} onOpenChange={setPreviewOpen}>
        <ExamPreview
          titleEs={form.title_es}
          titleEn={form.title_en}
          questions={form.question_ids.map((id) => questions.find((q) => q.question_id === id)).filter(Boolean)}
          passingScore={form.passing_score}
          timeLimit={form.time_limit_seconds}
          lang={lang}
        />
      </SellerPreviewModal>
    </motion.div>
  );
}
